import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import type { AvailableLocales } from '@/libs/i18n'
import type { IKratosSession } from '@/types/Kratos'

/**
 * The house app store without its JWT half: the ID portal's session is Kratos's HttpOnly cookie,
 * so "logged in" is a Kratos `whoami` round trip (`libs/auth.ts`), cached here for the page.
 */
export const useAppStore = defineStore('app', () => {
    const locale       = ref<AvailableLocales>(i18n.global.locale.value as AvailableLocales)
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const session      = ref<IKratosSession>()

    // Wraps the SAME state @nuxt/ui's vue plugin manages via useDark() — never a second source
    // of truth. No `emitAuto`: Light and Dark only (vue-spec `theme.md`).
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE (vue-spec `bootstrap.md` step 15).
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const isLoggedIn     = () => !!session.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    const setSession = (value: IKratosSession) => { session.value = value }
    const setLogout  = () => { session.value = undefined }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: AvailableLocales) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        i18n.global.locale.value = newLocale
        document.documentElement.lang = newLocale
    }

    return {
        locale, session, loaderActive, colorMode,
        isLoggedIn, isLoaderActive, isMobile,
        setSession, setLogout, startLoading, stopLoading, changeLocale,
    }
})
